import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '../../../lib/api';
import EmptyState from '../../../components/crm/EmptyState';
import { nyDateKey } from '../../../lib/datetime';

// /admin/system/errors (F13) — recent application errors recorded by
// reportError() into error_log. Read-only; admin + ops.

export default function Errors() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/admin/system/errors?limit=100');
      setErrors(res?.errors || []);
    } catch (err) {
      setError(err?.message || 'Failed to load errors');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white tracking-tight inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300" /> Errors
          </h1>
          <p className="text-[12px] text-gray-500 mt-0.5">Recent application errors (webhooks, crons, best-effort jobs).</p>
        </div>
        <button type="button" onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-300 hover:text-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>
      )}

      {!loading && errors.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="No errors recorded" description="Nothing has hit the error log recently. 🎉" />
      ) : (
        <div className="space-y-1.5">
          {errors.map((e) => (
            <div key={e.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <button type="button" onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                className="w-full text-left px-3 py-2.5 flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/[0.1] border border-amber-400/30 text-amber-300 shrink-0 mt-0.5 font-mono">
                  {e.source}
                </span>
                <span className="flex-1 min-w-0 text-[13px] text-white truncate">{e.message}</span>
                <span className="text-[10px] text-gray-500 shrink-0 mt-0.5 font-mono">{nyDateKey(e.createdAt)}</span>
              </button>
              {expanded === e.id && (
                <div className="px-3 pb-3 space-y-2 border-t border-white/[0.05] pt-2">
                  {e.context && (
                    <pre className="text-[11px] text-gray-400 bg-black/30 rounded p-2 overflow-x-auto">{JSON.stringify(e.context, null, 2)}</pre>
                  )}
                  {e.stack && (
                    <pre className="text-[11px] text-gray-500 bg-black/30 rounded p-2 overflow-x-auto whitespace-pre-wrap">{e.stack}</pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
